import { useState, useEffect, useMemo } from 'react';
import { DroppedDocument } from '../components/DocumentDropZone';

// Backend truncates the chat history it feeds into the regeneration prompt.
// Keep in sync with REGEN_CHAT_MESSAGE_LIMIT in
// riskninja-backend/src/routes/workflows.ts (regenerate-report route).
export const REGEN_CHAT_MESSAGE_LIMIT = 20;

export type SelectableDocumentType = 'original' | 'additional' | 'project';

export interface SelectableDocument {
  id: string;
  name: string;
  type: SelectableDocumentType;
  size?: number;
  policyType?: string;
  selected: boolean;
  status?: string;
  /** Project the document lives in — only populated for the 'project' bucket */
  projectName?: string;
  /** True when the document belongs to the same project as the report */
  sameProject?: boolean;
}

/**
 * A document that already exists in the customer/project but isn't part of
 * this report yet. Supplied by ProjectDocumentPicker.
 */
export interface ProjectDocumentOption {
  id: string;
  name: string;
  size?: number;
  policyType?: string;
  projectId?: string | null;
  projectName?: string;
  sameProject?: boolean;
}

export interface RegenerateSelection {
  selectedDocumentIds: string[];
  includeChatHistory: boolean;
  workflowType: string;
}

export interface OrganizedDocuments {
  original: SelectableDocument[];
  additional: SelectableDocument[];
  project: SelectableDocument[];
  totalSelected: number;
  totalAvailable: number;
  hasValidSelection: boolean;
}

interface UseRegenerateSelectionProps {
  workflowData: {
    documents: Array<{
      id: string;
      name: string;
      policyType?: string;
      size?: number;
    }>;
    workflowType: string;
  };
  additionalDocuments: DroppedDocument[];
  chatHistory: any[];
  /** Existing customer/project documents that can be pulled into the regeneration */
  projectDocuments?: ProjectDocumentOption[];
}

/**
 * Hook for managing document and chat selection in the regenerate modal
 */
export const useRegenerateSelection = ({
  workflowData,
  additionalDocuments,
  chatHistory,
  projectDocuments = []
}: UseRegenerateSelectionProps) => {
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [includeChatHistory, setIncludeChatHistory] = useState(false);

  // Initialize with all original + newly uploaded documents selected by default.
  // Documents pulled from elsewhere in the project stay unselected until the
  // user picks them explicitly.
  useEffect(() => {
    const defaultIds = [
      ...workflowData.documents.map(d => d.id),
      ...additionalDocuments
        .filter(d => d.status === 'completed')
        .map(d => d.id)
    ];
    setSelectedDocumentIds(prev => {
      const defaults = new Set(defaultIds);
      // Preserve any project-picker selections the user already made
      const preserved = prev.filter(id => !defaults.has(id));
      return [...defaultIds, ...preserved];
    });
  }, [workflowData.documents, additionalDocuments]);

  // Organize documents by type with selection state
  const organizedDocuments: OrganizedDocuments = useMemo(() => {
    const original: SelectableDocument[] = workflowData.documents.map(doc => ({
      id: doc.id,
      name: doc.name,
      type: 'original' as const,
      size: doc.size,
      policyType: doc.policyType,
      selected: selectedDocumentIds.includes(doc.id)
    }));

    const additional: SelectableDocument[] = additionalDocuments
      .filter(doc => doc.status === 'completed')
      .map(doc => ({
        id: doc.id,
        name: doc.name,
        type: 'additional' as const,
        size: doc.size,
        selected: selectedDocumentIds.includes(doc.id),
        status: doc.status
      }));

    const project: SelectableDocument[] = projectDocuments.map(doc => ({
      id: doc.id,
      name: doc.name,
      type: 'project' as const,
      size: doc.size,
      policyType: doc.policyType,
      projectName: doc.projectName,
      sameProject: doc.sameProject,
      selected: selectedDocumentIds.includes(doc.id)
    }));

    const totalSelected = selectedDocumentIds.length;
    const totalAvailable = original.length + additional.length + project.length;
    const hasValidSelection = totalSelected > 0 || includeChatHistory;

    return {
      original,
      additional,
      project,
      totalSelected,
      totalAvailable,
      hasValidSelection
    };
  }, [workflowData.documents, additionalDocuments, projectDocuments, selectedDocumentIds, includeChatHistory]);

  // Toggle document selection
  const toggleDocument = (docId: string) => {
    setSelectedDocumentIds(prev => 
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // Toggle chat history inclusion
  const toggleChatHistory = () => {
    setIncludeChatHistory(prev => !prev);
  };

  // Select all documents
  const selectAllDocuments = () => {
    const allDocIds = [
      ...organizedDocuments.original.map(d => d.id),
      ...organizedDocuments.additional.map(d => d.id),
      ...organizedDocuments.project.map(d => d.id)
    ];
    setSelectedDocumentIds(allDocIds);
  };

  // Deselect all documents
  const deselectAllDocuments = () => {
    setSelectedDocumentIds([]);
  };

  // Select all documents of a specific type
  const selectDocumentsByType = (type: SelectableDocumentType) => {
    const typeDocIds = organizedDocuments[type].map(d => d.id);
    const otherTypeIds = selectedDocumentIds.filter(id => 
      !typeDocIds.includes(id)
    );
    setSelectedDocumentIds([...otherTypeIds, ...typeDocIds]);
  };

  // Deselect all documents of a specific type
  const deselectDocumentsByType = (type: SelectableDocumentType) => {
    const typeDocIds = organizedDocuments[type].map(d => d.id);
    setSelectedDocumentIds(prev => 
      prev.filter(id => !typeDocIds.includes(id))
    );
  };

  // Get final selection for API call
  const getSelection = (): RegenerateSelection => ({
    selectedDocumentIds,
    includeChatHistory,
    workflowType: workflowData.workflowType
  });

  // Get selection summary for display
  const getSelectionSummary = () => {
    const originalSelected = organizedDocuments.original.filter(d => d.selected).length;
    const additionalSelected = organizedDocuments.additional.filter(d => d.selected).length;
    const projectSelected = organizedDocuments.project.filter(d => d.selected).length;
    
    return {
      originalSelected,
      additionalSelected,
      projectSelected,
      totalSelected: organizedDocuments.totalSelected,
      totalAvailable: organizedDocuments.totalAvailable,
      chatIncluded: includeChatHistory,
      chatMessageCount: chatHistory.length,
      chatMessagesUsed: Math.min(chatHistory.length, REGEN_CHAT_MESSAGE_LIMIT),
      chatMessageLimit: REGEN_CHAT_MESSAGE_LIMIT,
      hasValidSelection: organizedDocuments.hasValidSelection
    };
  };

  // Reset selection to defaults
  const resetSelection = () => {
    const allDocIds = [
      ...workflowData.documents.map(d => d.id),
      ...additionalDocuments
        .filter(d => d.status === 'completed')
        .map(d => d.id)
    ];
    setSelectedDocumentIds(allDocIds);
    setIncludeChatHistory(false);
  };

  return {
    // State
    selectedDocumentIds,
    includeChatHistory,
    organizedDocuments,
    
    // Actions
    toggleDocument,
    toggleChatHistory,
    selectAllDocuments,
    deselectAllDocuments,
    selectDocumentsByType,
    deselectDocumentsByType,
    resetSelection,
    
    // Getters
    getSelection,
    getSelectionSummary,
    
    // Computed values
    hasValidSelection: organizedDocuments.hasValidSelection,
    hasChatHistory: chatHistory.length > 0
  };
};

export default useRegenerateSelection;
